import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { comics } from '@/mocks/comics';

const siteUrl = 'http://iitzgd.cn/';

const chapters = Array.from({ length: 30 }, (_, i) => ({
  id: i + 1,
  title: `第${i + 1}话`,
  date: `2026-${String(6 - Math.floor(i / 5)).padStart(2, '0')}-${String(30 - (i % 30)).padStart(2, '0')}`,
}));

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <p className="text-foreground-500">漫画不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const sortedChapters = sortOrder === 'desc' ? [...chapters] : [...chapters].reverse();
  const pageUrl = `${siteUrl}/comic/${id}`;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是什么类型的漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}是一部${comic.tags.join('、')}题材的韩国漫画，由${comic.author}创作，目前评分为${comic.rating}分。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}在哪里可以免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在韩漫大全网站免费阅读${comic.title}的全部章节，支持下拉式观看模式，操作便捷且自动加载。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}更新频率如何？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}目前状态为${comic.status}，${comic.status === '连载中' ? '每周持续更新新章节' : `已完结，共${comic.totalChapters}话`}。`,
        },
      },
    ],
  };

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'ComicSeries',
      name: comic.title,
      url: pageUrl,
      description: comic.description,
      author: { '@type': 'Person', name: comic.author },
      inLanguage: 'zh-CN',
      datePublished: '2025-01-01',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: comic.tags[0] || '漫画', item: `${siteUrl}/category/${comic.category}` },
        { '@type': 'ListItem', position: 3, name: comic.title, item: pageUrl },
      ],
    },
    faqSchema,
  ];

  return (
    <>
      <SEOHead
        title={`${comic.title}_${comic.author}_韩国漫画在线免费阅读_韩漫大全`}
        description={`${comic.title}，${comic.author}创作的${comic.tags.join('、')}题材韩国漫画。${comic.description}免费下拉式阅读，最新章节实时更新。`}
        keywords={`${comic.title},${comic.author},韩国漫画,韩漫,${comic.tags.join(',')},免费漫画`}
        canonicalUrl={pageUrl}
        ogImage={comic.coverImageLarge}
        ogType="article"
        schemaData={schema}
      />
      <Navbar />
      <main>
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: comic.tags[0] || '漫画', to: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <section className="py-8 md:py-12 bg-background-50">
            <div className="mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-shrink-0 w-full lg:w-72">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={comic.coverImageLarge}
                      alt={comic.title}
                      title={`${comic.title} - 韩漫大全`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">{comic.title}</h1>
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="text-sm text-foreground-500">{comic.author}</span>
                    <span className="text-accent-500 font-medium text-lg">{comic.rating}分</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {comic.status === '连载中' ? `更新至${comic.updateChapter}话` : `全${comic.totalChapters}话`}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {comic.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/category/${comic.category}`}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs hover:bg-secondary-200 transition-colors whitespace-nowrap"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">{comic.description}</p>

                  <div className="flex items-center gap-4 mb-8 flex-wrap">
                    <Link
                      to={`/read/${comic.id}/1`}
                      className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                    >
                      开始阅读 第1话
                    </Link>
                    <Link
                      to={`/read/${comic.id}/${comic.updateChapter}`}
                      className="px-8 py-3 border border-primary-500 text-primary-500 rounded-full text-sm hover:bg-primary-50 transition-colors duration-200 whitespace-nowrap"
                    >
                      阅读最新话
                    </Link>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-lg text-foreground-900">章节目录</h2>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                      className="flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      <i className={`text-sm ${sortOrder === 'desc' ? 'ri-sort-desc' : 'ri-sort-asc'}`}></i>
                      {sortOrder === 'desc' ? '最新在前' : '最早在前'}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {sortedChapters.map((ch) => (
                      <Link
                        key={ch.id}
                        to={`/read/${comic.id}/${ch.id}`}
                        className="flex items-center justify-between px-3 py-2 rounded-md bg-background-100 hover:bg-primary-50 hover:text-primary-500 transition-colors duration-200 text-sm text-foreground-700 whitespace-nowrap"
                      >
                        <span>{ch.title}</span>
                        <span className="text-xs text-foreground-400">{ch.date}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-100">
            <div className="mx-auto px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <h2 className="font-heading text-xl text-foreground-900 mb-6">常见问题</h2>
                <div className="space-y-4">
                  {faqSchema.mainEntity.map((faq, idx) => (
                    <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 p-4">
                      <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-foreground-800">
                        {faq.name}
                        <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                        {faq.acceptedAnswer.text}
                      </p>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}